import moment from 'moment';
import i18n, { langCode } from '../i18n';

export const roundToNextMin = (date, minutes = 5) => {
  let updatedDate = date.clone().add(minutes, 'minutes');
  updatedDate.minutes(Math.floor(updatedDate.minutes() / minutes) * minutes);
  updatedDate.set('second', moment(0).get('second'));
  return updatedDate;
};

export const getStartTime = (date, time, minutes, minimalPrebookingTimeBumper) => {
  const today = moment();
  const updatedTime = moment(date || moment());
  updatedTime.set('hour', moment(time || moment()).get('hour'));
  updatedTime.set('minute', moment(time || moment()).get('minute'));
  updatedTime.set('second', moment(0).get('second'));
  updatedTime.set('millisecond', moment(time || moment()).get('millisecond'));

  if (moment(date).isSame(today, 'day') && moment(updatedTime).isBefore(moment().add(minimalPrebookingTimeBumper || 15, 'minutes'))) {
    return roundToNextMin(moment().add(minimalPrebookingTimeBumper || 15, 'minutes'), minutes);
  }
  return time ? moment(updatedTime) : roundToNextMin(moment(), minutes);
};

export const getMinSelectableDate = (presence) => {
  if (presence !== 'onsite' && presence !== 'notice') {
    return moment().toDate();
  }
  if (presence === 'notice') {
    const today3pm = moment().set('hour', 15).set('minute', 0).set('second', 0);
    if (moment().isAfter(today3pm)) {
      return moment().add(1, 'day').toDate();
    }
    return moment().toDate();
  }
  // On-site booking rule: before noon = next day, after noon = day after tomorrow
  const todayNoon = moment().set('hour', 12).set('minute', 0).set('second', 0);
  if (moment().isAfter(todayNoon)) {
    return moment().add(2, 'day').toDate();
  }
  return moment().add(1, 'day').toDate();
};

/**
 * Calculate earliest allowed prebooking date-time for a presence type.
 * Usergroup-then-contract resolution (inverse of duration pattern).
 *
 * Both the usergroup and the contract store bumpers in minutes (platform
 * usergroups.minimal_prebooking_time_bumper / minimal_on_site_prebooking_time_bumper and
 * contracts.min_pb_time_bumper / min_pb_on_site_time_bumper are all INTEGER minutes).
 * Unlike the duration fields on the same contract, they are NOT seconds — do not divide by 60.
 *
 * @param {string} presence - 'remote' or 'onsite'
 * @param {object} userGroup - Bumper settings in minutes
 * @param {object} contract - Bumper settings in minutes
 * @param {number} roundingMinutes - Time slot granularity (default 15)
 * @returns {moment|null} Earliest moment rounded to next slot, or null if no bumper
 */
export const getEarliestPrebookingDateTime = (
  presence, userGroup, contract, roundingMinutes = 15
) => {
  let bumperMinutes;

  if (presence === 'remote') {
    const ugBumper = userGroup?.minimalPrebookingTimeBumper;
    const cBumper = contract?.minPbTimeBumper ?? null;
    bumperMinutes = ugBumper ?? cBumper ?? 15;
  } else if (presence === 'onsite') {
    const ugBumper = userGroup?.minimalOnSitePrebookingTimeBumper;
    const cBumper = contract?.minPbOnSiteTimeBumper ?? null;
    bumperMinutes = ugBumper ?? cBumper;
    if (bumperMinutes === null || bumperMinutes === undefined) return null; // Fallback to getMinSelectableDate
  } else {
    return null;
  }

  const earliest = moment().add(bumperMinutes, 'minutes');
  const rounded = roundToNextMin(earliest, roundingMinutes);
  // Normalize milliseconds to match picker/validator behavior
  rounded.set('millisecond', 0);
  return rounded;
};


export const format = (secs) => {
  let hours = Math.floor(Math.floor(secs / 3600));
  let minutes = Math.floor(Math.floor((secs - (hours * 3600)) / 60));
  let seconds = Math.floor(secs - (hours * 3600) - (minutes * 60));

  if (hours < 10) {
    hours = '0' + hours;
  }
  if (minutes < 10) {
    minutes = '0' + minutes;
  }
  if (seconds < 10) {
    seconds = '0' + seconds;
  }

  return (hours > 0 ? (hours + ':') : '') + minutes + ':' + seconds;
};

export const isFirstTimeBeforeSecond = (firstTime, secondTime) => {
  const updatedTime = moment(firstTime);
  updatedTime.set('year', moment(secondTime).get('year'));
  updatedTime.set('month', moment(secondTime).get('month'));
  updatedTime.set('day', moment(secondTime).get('day'));

  return updatedTime.isBefore(moment(secondTime));
};

export const isToday = (dateString) => moment(dateString).isSame(new Date(), 'day');

export const getDurationTitle = (minutes) => {
  if (minutes < 60) {
    return i18n('minutes').replace('{}', minutes);
  }
  if (minutes === 60) {
    return i18n('hour').replace('{}', minutes / 60);
  }
  if (minutes % 60 === 0) {
    return i18n('hours').replace('{}', minutes / 60);
  }
  if (minutes / 60 < 2) {
    return i18n('hour_minutes').replace('{h}', Math.floor(minutes / 60)).replace('{m}', minutes % 60);
  }
  return i18n('hours_minutes').replace('{h}', Math.floor(minutes / 60)).replace('{m}', minutes % 60);
};

/**
 * Format date based on locale
 * Norwegian format: "27. november" (lowercase month)
 * Other formats: "27 November" (capitalized month)
 * @param {moment.Moment} date - The moment date object to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  const locale = langCode;
  const isNorwegian = locale === 'no' || locale === 'nb' || locale === 'nn';

  if (isNorwegian) {
    // Norwegian format: "27. november" with lowercase month
    return `${date.format('DD')}. ${date.format('MMMM').toLowerCase()}`;
  }

  // Default format: "27 November" with capitalized month
  return date.format('DD MMMM');
};
